"use client";

// THE AUTH STAGE — what you are about to walk into, living in the same room
// as the door. One screen, no panel, on a linear light in netti's colours.
//
// netti is the HERO netti (the /netti export the root cards are built on) in
// the door's blue, cyan and violet, rendered with PRESENCE (the engine keeps
// its paper creature on this ground: the layered washes, the pooled veins and
// the node cells, glowing), and it is bigger than the room: petal tip to petal
// tip it runs past the top and the bottom (~120% of the height), heart just in
// from the right edge. A soft radiance turns behind it (one pre-blurred image).
//
// As it turns it draws cells up from beneath it, round its flank, and they
// convert as they rise into orbs of its light that dissolve into the top edge
// (cells.ts). A few carry something: in the open water they OPEN into a pane of
// light whose OUTLINE is feathered while what it holds stays sharp, each with
// one small moving picture of what it says (a thread to netti, words being
// written, cells folding in).
//
// Phone: the form comes first. netti sits bottom-centre, its heart on the
// midline, ~125% of the width and out of focus (a body this big and unfocused
// is where it looks otherworldly). Scroll past the form and it comes into
// focus as it rises to the top of the footer; there the flow runs the other
// way: netti turns the other way, and the cells fall away beneath it, sweeping
// left to right, and the carrying cells come out from under it and open.
//
// Performance: the cells are ONE canvas (a sprite blit or two each); the few
// carrying cells are DOM moved by transform only. One rAF loop drives both,
// only while on screen and the tab is visible. netti is capped at 30fps on
// phones. Reduced motion gets a still frame.

import { useCallback, useEffect, useRef, useState } from "react";
import StageWater, { type WaterPlace } from "./StageWater";
import type { ColorState, RGB } from "../../lib/organism/color";
import { HERO_NETTI_COLOR, HERO_NETTI_GROUND, HERO_NETTI_LOOK } from "../../lib/organism/heroLook";
import "./auth-stage.css";

/** THE LOOK — the hero netti (John's /netti export, 2026-09-30): it lives in
 *  lib/organism/heroLook now, so the developers page wears the same creature. */
const NETTI_LOOK = HERO_NETTI_LOOK;

/** how far netti's body reaches, as a fraction of its canvas — the circle the
 *  cells flow around. Mirrors `--netti-reach` in auth-stage.css. */
const NETTI_REACH = 0.31;

const NETTI_COLOR: ColorState = HERO_NETTI_COLOR;

/** the ground netti is lit against — the key blue where its heart sits
 *  (`.auth-ground`, auth-stage.css). Fixed, like the look: the room no longer
 *  follows the wandering hue. */
const NETTI_GROUND: RGB = HERO_NETTI_GROUND; // ≈ hsl(210 73% 44%)

const WIDE = "(min-width: 861px)";

/** PHONE — where netti's heart comes to rest in the footer once it is in view:
 *  28svh down the screen, the footer (100svh) filling it once you reach it.
 *  Mirrors the transform in auth-stage.css. */
const NARROW_HEART_Y = 0.28;

export default function AuthStage() {
  const stageRef = useRef<HTMLDivElement>(null);
  const nettiRef = useRef<HTMLDivElement>(null);
  const nettiCanvasRef = useRef<HTMLCanvasElement>(null);

  // null until the client knows which room it is in
  const [wide, setWide] = useState<boolean | null>(null);
  useEffect(() => {
    const mq = window.matchMedia(WIDE);
    const on = () => setWide(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  // MOBILE — the footer reveal. Scroll-linked, rAF-throttled, one CSS var.
  useEffect(() => {
    if (wide !== false) return;
    const el = stageRef.current;
    if (!el) return;
    let raf = 0;
    const apply = () => {
      raf = 0;
      const vh = window.innerHeight || 1;
      const top = el.getBoundingClientRect().top;
      const r = Math.min(1, Math.max(0, (vh - top) / (vh * 0.85)));
      el.style.setProperty("--reveal", r.toFixed(3));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(apply); };
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
      el.style.removeProperty("--reveal");
    };
  }, [wide]);

  // NETTI — the exact look, on the engine directly.
  useEffect(() => {
    if (wide === null) return;
    const cv = nettiCanvasRef.current;
    if (!cv) return;
    let disposed = false;
    let fut: import("../../lib/organism/future/futureOrganism").FutureOrganism | null = null;
    let ro: ResizeObserver | null = null;
    (async () => {
      // the engine touches `document` at import time — client only, lazily
      const { FutureOrganism } = await import("../../lib/organism/future/futureOrganism");
      if (disposed) return;
      fut = new FutureOrganism({
        canvas: cv,
        maxDpr: 1,
        fpsCap: wide ? 0 : 30,
        tuners: NETTI_LOOK.tuners,
        age: NETTI_LOOK.age,
        color: NETTI_COLOR,
        bg: NETTI_GROUND,
        presence: NETTI_LOOK.presence,
        reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      });
      if (!fut.ok) return;
      fut.setRotation((NETTI_LOOK.spin * Math.PI) / 180);
      fut.resize();
      fut.start();
      ro = new ResizeObserver(() => fut?.resize());
      ro.observe(cv);
    })();
    return () => {
      disposed = true;
      ro?.disconnect();
      fut?.destroy();
    };
  }, [wide]);

  // THE WATER — the cells and the carrying cells (StageWater), told where
  // this netti stands: beside the form on a desktop, the footer's heart on a
  // phone.
  const locate = useCallback((layer: HTMLElement): WaterPlace | null => {
    const n = nettiRef.current?.getBoundingClientRect();
    if (!n) return null;
    const lr = layer.getBoundingClientRect();
    const r = n.width * NETTI_REACH;
    if (wide) {
      const body = document.querySelector(".auth-body");
      return {
        heart: { x: n.left + n.width / 2 - lr.left, y: n.top + n.height / 2 - lr.top, r },
        formRight: body ? body.getBoundingClientRect().right + 32 - lr.left : lr.width * 0.4,
      };
    }
    return { heart: { x: lr.width / 2, y: lr.height * NARROW_HEART_Y, r }, formRight: 0 };
  }, [wide]);

  return (
    <div ref={stageRef} className="auth-stage" data-room={wide === null ? undefined : wide ? "wide" : "narrow"} aria-hidden>
      <div className="auth-ground"><i /><b /></div>
      <div ref={nettiRef} className="auth-organism">
        <div className="auth-radiance" />
        <canvas ref={nettiCanvasRef} className="auth-netti" />
      </div>
      {wide !== null ? (
        <StageWater
          key={wide ? "wide" : "narrow"}
          room={wide ? "wide" : "narrow"}
          locate={locate}
          // desktop: the cells RISE past netti on the right · phone: they FALL
          // away beneath it, and the turn runs the other way (left to right)
          dir={wide ? -1 : 1}
          count={wide ? 150 : 64}
        />
      ) : null}
      <p className="auth-stage__foot">the internet, but yours.</p>
    </div>
  );
}
